import { useCallback } from "react";

import { useTranslation } from "react-i18next";

import { usePutCareEventBilling } from "api/hooks/usePutCareEventBilling";
import type { Status } from "api/schemas/CareEventBilling";
import { View } from "shared/containers/View";
import { createBreadCrumb } from "shared/containers/View/components/ViewBreadcrumbs";
import { reportError } from "utils/errorReporting";

import { BillingBulkActions, BillingTable, BillingTabs, CareEventDialog, StatusUpdateDialog } from "../components";
import { useBillingState } from "../hooks/useBillingState";

export function AdminBillingView() {
  const { t } = useTranslation();
  const {
    careEvent,
    error,
    filter,
    filteredNotes,
    isLoadingCareEvent,
    patient,
    patientLoading,
    selectedCareEvent,
    selectedCareEventIDs,
    setError,
    setFilter,
    setSelectedCareEvent,
    setSelectedCareEventIDs,
    setShowDialog,
    showCareEvent,
    showDialog,
    statusCounts,
  } = useBillingState();

  const putCareEventBilling = usePutCareEventBilling();
  const isUpdating = putCareEventBilling.isLoading;

  const handleTabChange = useCallback((value: Status) => {
    setFilter(value);
    setSelectedCareEventIDs([]);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setShowDialog({ show: false });
    setError(null);
  }, []);

  const handleUpdateStatus = useCallback(
    (status: Status) => {
      if (selectedCareEventIDs.length === 0) return;

      setError(null);
      putCareEventBilling.mutate(
        { data: { care_event_ids: selectedCareEventIDs, status } },
        {
          onSuccess: () => {
            setSelectedCareEventIDs([]);
            handleCloseDialog();
          },
          onError: e => {
            reportError("useBillingState", e);
            setError(t("errors.generic"));
          },
        }
      );
    },
    [handleCloseDialog, putCareEventBilling, selectedCareEventIDs, t]
  );

  const additionalBreadcrumbs = [createBreadCrumb(t("views.billing"), "/admin/billing")];

  return (
    <View
      title={t("views.billing")}
      headerContent={<BillingTabs filter={filter} statusCounts={statusCounts} onTabChange={handleTabChange} />}
      additionalBreadcrumbs={additionalBreadcrumbs}
    >
      <BillingTable
        bulkActions={
          <BillingBulkActions
            selectedCount={selectedCareEventIDs.length}
            onComplete={() => setShowDialog({ show: true, status: "completed" })}
            onDiscard={() => setShowDialog({ show: true, status: "discarded" })}
          />
        }
        data={filteredNotes}
        filter={filter}
        isLoadingMedicalNote={isLoadingCareEvent || patientLoading}
        selectedCareEvent={selectedCareEvent}
        selectedCareEventIDs={selectedCareEventIDs}
        onSelectCareEvent={setSelectedCareEvent}
        onSelectCareEventIDs={setSelectedCareEventIDs}
        onShowDialog={setShowDialog}
      />

      {careEvent && showCareEvent && patient && (
        <CareEventDialog
          careEvent={careEvent}
          selectedCareEvent={selectedCareEvent}
          onClose={() => {
            setSelectedCareEvent(null);
          }}
        />
      )}

      {showDialog.show && (
        <StatusUpdateDialog
          open
          status={showDialog.status}
          count={selectedCareEventIDs.length}
          isLoading={isUpdating}
          error={error}
          onConfirm={() => handleUpdateStatus(showDialog.status)}
          onCancel={handleCloseDialog}
        />
      )}
    </View>
  );
}
